import { useNavigate } from 'react-router-dom';
import { Search, ShieldCheck, IdCard, Clock, Bell } from 'lucide-react';
import { useAsync } from '../lib/useAsync';
import { store } from '../lib/store';
import { useMembers } from '../lib/members';
import { useAuth } from '../auth/AuthProvider';
import { IconChip, ListRow, Eyebrow, SkeletonList } from '../components/ui';
import { DOC_SIGLA, dateLongIT, timeLeftBadge } from '../lib/format';

export function HomeScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeId } = useMembers();
  const docs = useAsync(() => store.documents({ member_id: activeId ?? undefined }), [activeId]);
  const warr = useAsync(() => store.warranties({ member_id: activeId ?? undefined }), [activeId]);
  const alerts = useAsync(() => store.alerts(), []);

  const initial = (user?.display_name ?? '?').charAt(0).toUpperCase();
  const alertCount = alerts.data?.summary.within_60 ?? 0;
  const expiring = warr.data?.items.find((w) => w.status === 'expiring');
  const recent = (docs.data ?? []).slice(0, 3);

  return (
    <div className="screen">
      {/* Header */}
      <div className="topbar">
        <div>
          <Eyebrow>Buongiorno</Eyebrow>
          <h1 className="h1">Il tuo archivio</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            className="iconbtn"
            style={{ position: 'relative' }}
            onClick={() => navigate('/scadenze')}
            aria-label="Avvisi di scadenza"
          >
            <Bell size={20} />
            {alertCount > 0 && <span className="bell-dot" />}
          </button>
          <button className="avatar" onClick={() => navigate('/profilo')} aria-label="Profilo">
            {initial}
          </button>
        </div>
      </div>

      {/* Search (priorità) */}
      <button className="searchbar" onClick={() => navigate('/cerca')} aria-label="Cerca un documento">
        <Search size={18} />
        <span style={{ color: 'var(--muted)' }}>Cerca un documento…</span>
      </button>

      {/* 2 tile */}
      <div className="tiles" style={{ marginTop: 18 }}>
        <button className="card tile" onClick={() => navigate('/documenti')}>
          <IconChip sm>
            <IdCard size={20} />
          </IconChip>
          <div>
            <div className="tile__count">{docs.data?.length ?? '—'}</div>
            <div className="tile__label">Identità</div>
          </div>
        </button>
        <button className="card tile" onClick={() => navigate('/garanzie')}>
          <IconChip sm>
            <ShieldCheck size={20} />
          </IconChip>
          <div>
            <div className="tile__count">{warr.data?.summary.active ?? '—'}</div>
            <div className="tile__label">Garanzie attive</div>
          </div>
        </button>
      </div>

      {/* Recenti */}
      <div className="section-head">
        <Eyebrow>Recenti</Eyebrow>
        <button className="section-head__link" onClick={() => navigate('/documenti')}>
          Tutti
        </button>
      </div>
      {docs.loading ? (
        <SkeletonList rows={3} />
      ) : (
        <div className="list">
          {recent.map((d) => (
            <ListRow
              key={d.id}
              chip={<IconChip>{DOC_SIGLA[d.type]}</IconChip>}
              title={d.title}
              meta={d.holder}
              onClick={() => navigate(`/documenti/${d.id}`)}
            />
          ))}
        </div>
      )}

      {/* Alert garanzia in scadenza */}
      {expiring && (
        <button
          className="alert"
          style={{ marginTop: 18, width: '100%', textAlign: 'left' }}
          onClick={() => navigate(`/garanzie/${expiring.id}`)}
        >
          <Clock size={22} className="alert__icon" />
          <div>
            <div style={{ fontWeight: 600 }}>
              {expiring.product} · garanzia
            </div>
            <div style={{ fontSize: 13 }}>
              Scade tra {expiring.days_left} giorni · {dateLongIT(expiring.expires_at)}
            </div>
          </div>
          <span className="badge badge--accent" style={{ marginLeft: 'auto' }}>
            {timeLeftBadge(expiring.days_left)}
          </span>
        </button>
      )}
    </div>
  );
}
